import _ from 'lodash'
import {
    XmlElement,
    type XmlAst,
    type XmlAttributeKey,
    type XmlAttributeValue,
    type XmlAttributes,
    type XmlFile,
    type XmlNode,
    XML_RESERVED_CHARS
} from './constants'
import {FileBuilder} from '../../utils/fileBuilder'
import type {PartialComponentExport} from '../constants'
import {createCharReplacementFunc} from '../../utils/utils'
import type {WmlFileContent} from '@wix/document-services-types'

const generateXmlElement = (
    componentId: PartialComponentExport['structure']['rootComponent'],
    componentsExport: PartialComponentExport['structure']['components']
): XmlElement => {
    const {type, components, ...attributes} = componentsExport[componentId]

    const element: XmlElement = {
        type: 'element',
        tag: type,
        attributes: {},
        children: []
    }

    // TODO: add attributes serialize {@see DM-8917}
    element.attributes = _.mapValues(attributes, attribute =>
        // eslint-disable-next-line lodash/prefer-lodash-typecheck
        typeof attribute !== 'string' ? JSON.stringify(attribute) : attribute
    )

    if (components?.length) {
        element.children = components.map((childId: string) => generateXmlElement(childId, componentsExport))
    } else {
        const {label, text} = element.attributes
        if (label) {
            element.children.push({type: 'text', value: label})
            delete element.attributes.label
        } else if (text) {
            element.children.push({type: 'cdata', value: text})
            delete element.attributes.text
        }
    }

    return element
}

const replaceReservedChars = createCharReplacementFunc(XML_RESERVED_CHARS)

const buildAttribute = (value: XmlAttributeValue, key: XmlAttributeKey): string => {
    return `${key}="${replaceReservedChars(value.toString().trim())}"`
}

const buildAttributes = (attributes?: XmlAttributes): string => {
    if (!attributes || _.isEmpty(attributes)) {
        return ''
    }

    return ` ${_.map(attributes, buildAttribute).join(' ')}`
}

const appendNode = (fileBuilder: FileBuilder, node: XmlNode) => {
    const {type} = node
    if (type === 'element') {
        const {tag, attributes, children} = node
        const hasChildren = children.length > 0

        fileBuilder.writeLine(`<${tag}${buildAttributes(attributes)}${hasChildren ? '>' : ' />'}`)

        if (hasChildren) {
            fileBuilder.indent()
            for (const child of children) {
                appendNode(fileBuilder, child)
            }

            fileBuilder.dedent()
            fileBuilder.writeLine(`</${tag}>`)
        }
    } else if (type === 'text') {
        const {value} = node
        fileBuilder.writeLine(replaceReservedChars(value.trim()))
    } else if (type === 'cdata') {
        const {value} = node
        fileBuilder.writeLine(`<![CDATA[${value}]]>`)
    }
}

export const generateXmlAst = ({rootComponent, components}: PartialComponentExport['structure']): XmlAst =>
    generateXmlElement(rootComponent, components)

export const convertXmlAstToFile = (xmlAst: XmlAst): WmlFileContent => {
    const fileBuilder = new FileBuilder()
    appendNode(fileBuilder, xmlAst)
    return fileBuilder.toString()
}

export const generateXmlFile = (componentExport: PartialComponentExport['structure']): XmlFile => ({
    type: 'xml',
    content: convertXmlAstToFile(generateXmlAst(componentExport))
})
